import {CONFIG} from '../../appConfig';
import STATE from '../../constants/state';

const ticket = 'ticket/';

export default {
  /**
   * @desc clears current Ticket data in session
   * @param {Object} data {params}
   * @returns {Object} - Object contains property with route to go to and cleared Ticket data session
   * {
   *   go_to : {...route to go to after session clears},
   *   session: {...session with cleared ticket data}
   * }
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.clearCurrentSession();
   * // dispatched results stored in `state.gmi.session.ticket`
   */
  clearCurrentTicketSession(data = {}) {
    const {params = {}, payload = {}} = data;
    return {
      endpoint: `${ticket}session/clear`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Initiate Ticket Check-In
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.checkInInitiate();
   */
  checkInInitiate(data = {}) {
    const {params = {}} = data;
    return {
      endpoint: `${ticket}initiate`,
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      params
    };
  },

  /**
   * @desc Look up for a driver profile using license, country and/or county. And, add the response in session
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.driverLookup();
   * // dispatched results stored in `state.gmi.session.ticket.driver_info`
   */
  driverLookup(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}driver/searchByDriverLicense`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Commit the driver details that's entered or confirmed the pre-populated ones
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.driverDetailsCommit();
   * // dispatched results stored in `state.gmi.session.ticket.driver_info`
   */
  driverDetailsCommit(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}driver/confirm`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Commit the Reservation details of rental pickup time
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.reservationDetailsCommit();
   * // dispatched results stored in `state.gmi.session.ticket.pickup_time`
   */
  reservationDetailsCommit(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}details/confirm`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Commit the Protection Addons added and/or removed the pre-selected ones
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.protectionAddonsCommit();
   * // dispatched results stored in `state.gmi.session.ticket.car_class_details.vehicle_rates.extras.insurance`
   */
  protectionAddonsCommit(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}extras/protections`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Submit the Protection Addons added and/or removed the pre-selected ones
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.protectionAddonsSubmit();
   * // dispatched results stored in `state.gmi.session.ticket.car_class_details.vehicle_rates.extras.insurance`
  */
  protectionAddonsSubmit(data = {}) {
    const {params={}} = data;
    return {
      endpoint: `${ticket}extras/protections/submit`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      params
    };
  },

  /**
   * @desc Commit the Equipment Addons added and/or removed the pre-selected ones
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.extrasAddonsCommit();
   * // dispatched results stored in `state.gmi.session.ticket.car_class_details.vehicle_rates.extras.equipment`
   */
  extrasAddonsCommit(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}extras/equipment`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Submit the Extras Addons added and/or removed the pre-selected ones
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.extrasAddonsSubmit();
   * // dispatched results stored in `state.gmi.session.ticket.car_class_details.vehicle_rates.extras.equipment`
  */
  extrasAddonsSubmit(data = {}) {
    const {params={}} = data;
    return {
      endpoint: `${ticket}extras/equipment/submit`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      params
    };
  },

  /**
   * @desc Retrieves a check-in reservation using rental confirmation number, last and first name.
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.retrieveReservation();
   * // dispatched results stored in `state.gmi.session.ticket.reservation`
   */
  retrieveReservation(data = {}) {
    const {params = {}} = data;
    return {
      endpoint: `${ticket}retrieve`,
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      params
    };
  },

  /**
   * @desc Get Terms and Conditions of the check-in rental
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.getTermsAndConditions();
   * // dispatched results stored in `state.gmi.gma.ticket.checkIn.termsAndConditions`
   */
  getTermsAndConditions(data = {}) {
    const {params = {}} = data;
    return {
      endpoint: `${ticket}termsAndConditions`,
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      statePath: STATE.GMA.TICKET.TICKET_TERMS_AND_CONDITIONS,
      params
    };
  },

  /**
   * @desc Call the IBM payment services to get card submission_url to add a credit card
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.paymentInitiate();
   * // dispatched results stored in `state.gmi.gma.ticket.checkIn.payment.ibmData`
   */
  paymentInitiate(data = {}) {
    const {params={}, payload = {}} = data;
    return {
      endpoint: `${ticket}payment/initiate`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      statePath: STATE.GMA.TICKET.TICKET_PAYMENT_INITIATE,
      data: payload,
      params
    };
  },

  /**
   * @desc Call to get the added credit card details
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.getPaymentCardDetails();
   * // dispatched results stored in `state.gmi.gma.ticket.checkIn.payment.cardDetails`
   */
  getPaymentCardDetails(data = {}) {
    const {params = {}, payload = {}} = data;
    return {
      endpoint: `${ticket}payment/recordPaymentTransaction`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      statePath: STATE.GMA.TICKET.TICKET_PAYMENT_CARD_DETAILS,
      data: payload,
      params
    };
  },

  /**
   * @desc Commit the Payment details added and go to Terms & Conditions page
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.ticket
   * @example
   * services.gma.ticket.paymentConfirm();
   * // dispatched results stored in `state.gmi.session.ticket.driver_info.payment_profile`
   */
  paymentConfirm(data = {}) {
    const { params={}, payload = {} } = data;
    return {
      endpoint: `${ticket}payment/confirm`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      data: payload,
      params
    };
  },

  /**
   * @desc Submit the Terms & Conditions details and go to confirmation page
   * @returns {Object}
   * @memberof services.gma.ticket
   */
  termsConditionsCommit(data = {}) {
    const {params={}} = data;
    return {
      endpoint: `${ticket}commit`,
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      params
    };
  },

  /**
   * @desc Acknowledge the young renter fee
   * @returns {Object}
   * @memberof services.gma.ticket
   * services.gma.ticket.ackYoungFee();
   * results stored in - session.check_in.young_renter_fee_modal
   */
  ackYoungFee(data = {}) {
      const {params={}} = data;
      return {
        endpoint: `${ticket}driver/ackYoungFee`,
        method: 'POST',
        origin: CONFIG.GMA_ORIGIN_URL,
        params
      };
    }
};
